import { useEffect, useRef, useState, type ReactNode } from 'react';

import { ExtractionActivityIndicator } from '@/components/extraction-runs/extraction-activity-indicator';
import { FolioTriggerButton } from '@/components/folio/folio-trigger-button';
import { SidebarTrigger, useSidebar } from '@/components/ui/sidebar';
import { UploadActivityIndicator } from '@/components/upload-activity-indicator';
import { cn } from '@/lib/utils';
import { AppBreadcrumb } from './app-breadcrumb';

export interface AppLayoutHeaderProps {
  /** Receives the element a page's breadcrumb tail portals into. */
  readonly onBreadcrumbTail: (element: HTMLElement | null) => void;
  /** Receives the element a page's actions portal into. */
  readonly onPageActions: (element: HTMLElement | null) => void;
}

/** The app header with the shell's own utilities: upload and extraction activity, and Sean. */
export function AppLayoutHeader({ onBreadcrumbTail, onPageActions }: AppLayoutHeaderProps): ReactNode {
  return (
    <AppLayoutHeaderView
      onBreadcrumbTail={onBreadcrumbTail}
      onPageActions={onPageActions}
      utilities={
        <>
          <UploadActivityIndicator />
          <ExtractionActivityIndicator />
          <FolioTriggerButton />
        </>
      }
    />
  );
}

export interface AppLayoutHeaderViewProps extends AppLayoutHeaderProps {
  /** The shell's utilities at the header's end, after the page's own actions. */
  readonly utilities: ReactNode;
}

/**
 * The header's layout: sidebar toggle, breadcrumb and its tail, then the
 * page's actions and the utilities. It reads only the sidebar and the route,
 * so the utilities decide for themselves what session state they need.
 */
export function AppLayoutHeaderView({ onBreadcrumbTail, onPageActions, utilities }: AppLayoutHeaderViewProps): ReactNode {
  const { isMobile, open, openMobile } = useSidebar();
  const isSidebarOpen = isMobile ? openMobile : open;
  const [scrolled, setScrolled] = useState(false);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const main = document.getElementById('main-content');
    if (!main) return;

    const updateScrolled = () => {
      const nextScrolled = main.scrollTop > 8;
      setScrolled((currentScrolled) => (currentScrolled === nextScrolled ? currentScrolled : nextScrolled));
      frameRef.current = null;
    };

    updateScrolled();

    const onScroll = () => {
      if (frameRef.current !== null) {
        return;
      }

      frameRef.current = window.requestAnimationFrame(updateScrolled);
    };

    main.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      main.removeEventListener('scroll', onScroll);
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  return (
    <header
      className={cn(
        // The container page actions query, so their labels yield to the
        // header's own width rather than the window's.
        '@container/header sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 px-6',
        'transition-[background-color,box-shadow,backdrop-filter] duration-300 ease-out-quart',
        'motion-reduce:transition-none',
        scrolled
          ? 'bg-background/95 shadow-xs backdrop-blur-md'
          : 'bg-background/80 backdrop-blur-sm',
      )}
    >
      <SidebarTrigger
        size="icon"
        aria-expanded={isSidebarOpen}
        aria-label="Toggle navigation"
        className="-ml-2"
      />
      <div
        data-slot="app-layout-breadcrumb"
        className="min-w-0 shrink-[100] overflow-hidden @max-[24rem]/header:hidden [&_[data-slot=breadcrumb-item]:last-child]:min-w-0 [&_[data-slot=breadcrumb-item]:last-child_[data-slot=breadcrumb-page]]:block [&_[data-slot=breadcrumb-item]:last-child_[data-slot=breadcrumb-page]]:truncate [&_[data-slot=breadcrumb-list]]:flex-nowrap [&_[data-slot=breadcrumb-list]]:overflow-hidden"
      >
        <AppBreadcrumb />
      </div>
      {/* A page's breadcrumb tail (see PageBreadcrumbTail) lands here, beside the
          breadcrumb rather than inside it. When the
          header runs short the breadcrumb gives way first, then the tail, down
          to its controls' own minimum and never under the actions, so a page's
          actions stay whole beside Sean. Below a 24rem header the breadcrumb
          and the utilities' labels leave the row and the page actions close
          up, which keeps the tail's controls whole at the narrowest docked
          header (a 1024px window beside the widest Sean panel, about 272px). */}
      <div
        ref={onBreadcrumbTail}
        data-slot="app-layout-breadcrumb-tail"
        className="flex items-center gap-1.5 empty:hidden"
      />
      <div data-slot="app-layout-actions" className="relative z-10 ml-auto flex shrink-0 items-center gap-1">
        {/* The page's own actions (see PageActions) lead the shell's utilities. */}
        <div
          ref={onPageActions}
          data-slot="app-layout-page-actions"
          className="mr-2 flex items-center gap-2 empty:hidden @max-[24rem]/header:mr-0"
        />
        {utilities}
      </div>
    </header>
  );
}
